import { useState, useEffect, useCallback } from 'react';
import { WIX_INSTANCE_PARAMS } from '~actions/component_actions';
import axios from 'axios';
import { Form, Spinner } from 'react-bootstrap';
import { List, fromJS } from 'immutable';
import styles from 'widget/event_components/ticket-spot.scss';
import accessCodeStyle from './access-code.scss';
import * as E from './elements';

export const AccessCode = ({
  settings,
  event,
  cart,
  tickets,
  handleTicketUpdate,
  handleInitTicketStateChange,
  onResizeComponent,
  handleUpdateTicketsList,
}) => {
  const keepAccessCodeOpen = settings.get('keepAccessCodeOpen');

  const [value, setValue] = useState();
  const [loading, setLoading] = useState(false);
  const [showInput, setShowInput] = useState(keepAccessCodeOpen);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    setShowInput(keepAccessCodeOpen);
    onResizeComponent && onResizeComponent();
  }, [keepAccessCodeOpen]);

  const trackAccessCodeEvent = (eventName, eventData) => {
    if (window.gtag) {
      window.gtag('event', eventName, {
        event_category: 'AccessCode',
        ...eventData,
      });
    }

    if (window.fbq) {
      window.fbq('trackCustom', eventName, eventData);
    }
  };

  const handleAccessCode = useCallback(async () => {
    setLoading(true);
    setIsError(false);
    handleInitTicketStateChange();
    try {
      const url = `${process.env.EVENTVIEWER_SERVER}/api/v1/events/${event.get('id')}/tickets/access-code${WIX_INSTANCE_PARAMS}`;
      const result = await axios.post(url, { access_code: value }, { withCredentials: true });

      if (!result.data || !result.data.length) {
        throw new Error(`Invalid access code`);
      }

      // Convert new tickets to Immutable List
      const newTickets = List(fromJS(result.data));

      // Filter out duplicate tickets by ID
      const existingTicketIds = tickets.map((t) => t.get('id')).toSet();
      const uniqueNewTickets = newTickets.filter((ticket) => !existingTicketIds.has(ticket.get('id')));

      // Prepend new tickets to existing tickets
      const updatedTickets = uniqueNewTickets.concat(tickets);

      // Update the ticket list
      handleUpdateTicketsList(updatedTickets);

      // Update the access code in state
      handleTicketUpdate({ access_code: value });

      trackAccessCodeEvent('ApplyAccessCode', {
        event_label: value,
        currency: event.get('currency'),
        content_name: event.get('name'),
        content_category: 'AccessCode',
        content_ids: [value],
      });
    } catch (e) {
      setIsError(true);
      handleTicketUpdate({ access_code: null });
      trackAccessCodeEvent('AccessCodeError', {
        event_label: value,
        error_message: e.message || 'Access code application failed',
        content_name: event.get('name'),
        content_category: 'AccessCode',
        content_ids: [value],
      });
    }
    setLoading(false);
  }, [value, cart, tickets]);

  return (
    <Form.Group controlId="accessCodeInput" className={accessCodeStyle.container}>
      {!keepAccessCodeOpen && (
        <Form.Label
          className="apply-access-code-text"
          onClick={() => keepAccessCodeOpen === false && setShowInput(!showInput)}
          style={{ cursor: 'pointer' }}
        >
          {settings.get('accessCodeText') || 'Access code?'}
        </Form.Label>
      )}
      <div>
        {showInput && (
          <E.AccessCode>
            {keepAccessCodeOpen && <E.Label className="apply-access-code-text">{settings.get('accessCodeText') || 'Have an access code?'}</E.Label>}
            <E.CustomInputGroup>
              <E.StyledFormControl
                className={styles.promoInput}
                onChange={(e) => setValue(e.target.value)}
                disabled={loading}
                placeholder={settings.get('accessCodePlaceholderText') || 'Enter access code'}
              />
              <E.CustomButton variant="text" onClick={handleAccessCode} disabled={loading} className="access-code-apply-button">
                {loading ? <Spinner animation="border" size="sm" /> : 'Apply'}
              </E.CustomButton>
            </E.CustomInputGroup>
          </E.AccessCode>
        )}
      </div>
      {isError && <div className={styles.errorDetails}>Sorry, that access code is invalid</div>}
    </Form.Group>
  );
};
